import { defineStore } from 'pinia'
import type { UserInfo, CompanyInfo } from '../types/store'
import $api from '@/api/index'
import { getNearestPark } from '@/utils/tool'

// 声明getCurrentPages函数
declare function getCurrentPages(): any[]

interface LoginParams {
  username: string
  password: string
}

interface WxPhoneLoginParams {
  code: string
  encrypted_data: string
  iv: string
  [key: string]: any
}

interface Coordinate {
  latitude: string
  longitude: string
}

interface UserState {
  token: string
  userInfo: UserInfo | null
  companyInfo: CompanyInfo | null
  permissions: string[]
  roles: string[]
  currentPark: any | null
  parkSetting: any | null
  coordinate: Coordinate | null
  redirectUrl: string | null
  site_id: string | number
}

interface ApiResponse<T = any> {
  data: T
  [key: string]: any
}

declare global {
  interface Uni {
    $api: {
      user: {
        login(params: LoginParams): Promise<ApiResponse<{ token: string; userInfo: UserInfo }>>
        getPermissions(): Promise<ApiResponse<{ permissions: string[]; roles: string[] }>>
        getUserInfo(): Promise<ApiResponse<UserInfo>>
      }
    }
  }
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: uni.getStorageSync('access_token') || '',
    userInfo: uni.getStorageSync('userInfo') || null,
    companyInfo: uni.getStorageSync('companyInfo') || null,
    permissions: [],
    roles: [],
    currentPark: uni.getStorageSync('currentPark') || null,
    parkSetting: uni.getStorageSync('parkSetting') || null,
    coordinate: uni.getStorageSync('coordinate') || null,
    redirectUrl: uni.getStorageSync('redirectUrl') || null,
    site_id: uni.getStorageSync('site_id') || '',
  }),

  getters: {
    avatar: (state: UserState): string => {
      return state.userInfo?.avatar || '/static/images/default-avatar.png'
    },
    hasPermission: (state: UserState) => {
      return (permission: string): boolean => {
        return state.permissions.includes(permission)
      }
    },
    hasRole: (state: UserState) => {
      return (role: string): boolean => {
        return state.roles.includes(role)
      }
    },
    isLogin: (state: UserState): boolean => {
      return state.token !== ''
    },
    // 绑定企业
    isAuth: (state: UserState): boolean => {
      return state.companyInfo !== null
    },
    // 是否管理员 1: 管理员 2: 普通用户
    isAdmin: (state: UserState): boolean => {
      return state.userInfo?.role === 1
    }
  },

  actions: {
    setToken(token: string): void {
      this.token = token
      uni.setStorageSync('access_token', token)
    },
    setSiteId(site_id: string): void {
      this.site_id = site_id
      uni.setStorageSync('site_id', site_id)
    },

    setUserInfo(userInfo: UserInfo | null): void {
      this.userInfo = userInfo
      uni.setStorageSync('userInfo', userInfo)
    },

    setCompanyInfo(companyInfo: CompanyInfo | null): void {
      this.companyInfo = companyInfo
      uni.setStorageSync('companyInfo', companyInfo)
    },

    setPermissions(permissions: string[]): void {
      this.permissions = permissions
    },

    setRoles(roles: string[]): void {
      this.roles = roles
    },

    setCurrentPark(currentPark: any | null): void {
      this.currentPark = currentPark
      uni.setStorageSync('currentPark', currentPark)
    },

    setParkSetting(parkSetting: any | null): void {
      this.parkSetting = parkSetting
      uni.setStorageSync('parkSetting', parkSetting)
    },

    setCoordinate(coordinate: Coordinate | null): void {
      this.coordinate = coordinate
      uni.setStorageSync('coordinate', coordinate)
    },

    setRedirectUrl(redirectUrl: string | null): void {
      this.redirectUrl = redirectUrl
      if (redirectUrl) {
        uni.setStorageSync('redirectUrl', redirectUrl)
      } else {
        uni.removeStorageSync('redirectUrl')
      }
    },

    // async login(params: LoginParams) {
    //   try {
    //     const res = await uni.$api.user.login(params);
    //     this.setToken(res.data.token);
    //     this.setUserInfo(res.data.userInfo);
    //     await this.getUserPermissions();
    //     return res;
    //   } catch (error) {
    //     console.error('登录失败', error);

    //   }
    // },

    async wxPhoneLogin(params: WxPhoneLoginParams) {
      try {
        const res = await $api.common.login(params)
        this.setToken(res.data.token)
        await this.getUserInfo()
        return res
      } catch (error) {
        console.error('快捷登录失败', error)
        throw error
      }
    },

    async getUserInfo() {
      try {
        const res = await $api.common.getSelfInfo({ park_id: this.currentPark?.id })
        this.setUserInfo(res.data.user)
        this.setCompanyInfo(res.data.company)
        return res.data
      } catch (error) {
        console.error('获取用户信息失败', error)
        throw error
      }
    },

    async getUserPermissions() {
      try {
        const res = await uni.$api.user.getPermissions()
        this.setPermissions(res.data.permissions)
        this.setRoles(res.data.roles)
        return res
      } catch (error) {
        console.error('获取用户权限失败', error)
        throw error
      }
    },

    async getCurrentPark() {
      try {
        if (!this.currentPark) {
          const nearestPark = await this.queryNearestPark()
          // 获取园区设置
          if (nearestPark) {
            const settingRes = await $api.home.getParkSetting({ park_id: nearestPark.id })
            this.setParkSetting(settingRes.data)
          }
        }
        return this.currentPark
      } catch (error) {
        console.error('获取当前园区失败', error)
        throw error
      }
    },

    async queryNearestPark() {
      try {
        const res = await $api.home.getParks()
        const nearestPark = await getNearestPark(res.data)
        this.setCurrentPark(nearestPark)
        return nearestPark
      } catch (error) {
        console.error('获取最近园区失败', error)
        throw error
      }
    },

    async getCoordinate() {
      return new Promise((resolve, reject) => {
        uni.getLocation({
          type: 'gcj02',
          success: (res) => {
            this.setCoordinate({
              latitude: res.latitude.toString(),
              longitude: res.longitude.toString()
            })
            resolve({ latitude: res.latitude, longitude: res.longitude })
          },
          fail: (err) => {
            console.error('获取坐标失败', err)
            uni.showToast({ title: '获取地理坐标失败，请检查权限', icon: 'none' })
            reject(err)
          }
        })
      })
    },

    async logout(): Promise<boolean> {
      try {
        await $api.common.logout()
        this.setToken('')
        this.setUserInfo(null)
        this.setCompanyInfo(null)
        this.setCurrentPark(null)
        this.setParkSetting(null)
        this.setPermissions([])
        this.setRoles([])
        this.setCoordinate(null)
        this.setSiteId('')
        return true
      } catch (error) {
        console.error('退出登录失败', error)
        throw error
      }
    },

    async refreshUserInfo() {
      try {
        const res = await uni.$api.user.getUserInfo()
        this.setUserInfo(res.data)
        return res
      } catch (error) {
        console.error('刷新用户信息失败', error)
        throw error
      }
    },

    /**
     * 保存当前页面路径
     */
    saveCurrentPagePath(): void {
      const pages = getCurrentPages()
      if (pages.length > 0) {
        const currentPage = pages[pages.length - 1]
        const currentRoute = currentPage.route

        // 如果是登录页面，不执行保存
        if (currentRoute === 'pages/auth/login') {
          return
        }

        const currentOptions = (currentPage as any).options || {}

        // 构建完整的页面路径和参数
        let fullPath = `/${currentRoute}`
        const queryParams = Object.keys(currentOptions)
          .map((key) => `${key}=${currentOptions[key]}`)
          .join('&')

        if (queryParams) {
          fullPath += `?${queryParams}`
        }

        this.setRedirectUrl(fullPath)
      }
    },

    /**
     * 获取保存的页面路径并清除
     */
    getAndClearRedirectUrl(): string | null {
      const redirectUrl = this.redirectUrl
      this.setRedirectUrl(null)
      return redirectUrl
    },

    /**
     * 根据页面路径获取对应的tabbar名称
     */
    getTabbarNameByPath(path: string): string {
      if (path.includes('/pages/home/')) {
        return '首页'
      } else if (path.includes('/pages-park/')) {
        return '园区服务'
      } else if (path.includes('/pages-enterprise/')) {
        return '企业服务'
      } else if (path.includes('/pages-industry/')) {
        return '产业服务'
      } else if (path.includes('/pages-property/')) {
        return '物业服务'
      } else if (path.includes('/pages/mine/')) {
        return '我的'
      }
      return '首页'
    }
  }
})
